// WHAT: Context provider for Oura health data with computed metrics
// WHY:  Provides global access to Oura data with lazy loading and computed HRV/heart rate metrics
// DOMAIN: Health → Oura
// DEPENDENCIES: getLatestOuraEntry service, OuraDailyEntry type
// RISKS:
//   - NOTE: Computed values are typed as any - may cause type safety issues
//   - NOTE: Lazy loading means data may not be available on mount
//   - NOTE: HRV calculations may not handle edge cases (empty arrays, null values)
//   - NOTE: refreshData function is not implemented - may cause runtime errors

import type {
  OuraDailyEntry,
  OuraReadiness,
  OuraSleep,
  OuraActivity,
  OuraHeartRate,
  OuraFullSleep,
  OuraSpo2,
  OuraStress,
  OuraResilience,
  OuraVo2Max,
} from "../data/ouraTypes";

import React, { createContext, useContext, useCallback, useState, type ReactNode } from "react";

import { getLatestOuraEntry } from "@/domains/health/oura/services";

type LatestEntryFetcher = () => Promise<null | OuraDailyEntry>;
const safeGetLatestOuraEntry: LatestEntryFetcher = getLatestOuraEntry as LatestEntryFetcher;

/**
 * WHAT:
 *   Context type definition for Oura data access.
 *
 * WHY:
 *   Provides interface for consuming Oura data throughout the application.
 */
export interface OuraDataContextType {
  activity: OuraActivity | undefined;
  averageHeartRate: number | undefined;
  // NOTE: Computed metrics
  averageHRV: number | undefined;

  // NOTE: Main data
  data: null | OuraDailyEntry;
  error: null | string;
  fullSleep: OuraFullSleep | undefined;
  heartrate: OuraHeartRate | undefined;
  heartRateMeasurements: (null | number)[];
  hrvMeasurements: (null | number)[];
  loading: boolean;
  maxHRV: number | undefined;
  // NOTE: Computed values from OuraDailyEntry
  readiness: OuraReadiness | undefined;

  refreshData: () => void;
  // NOTE: Actions
  reload: () => Promise<void>;
  resilience: OuraResilience | undefined;
  sleep: OuraSleep | undefined;
  spo2: OuraSpo2 | undefined;

  stress: OuraStress | undefined;
  vascularAge: OuraVo2Max | undefined;
}

const OuraDataContext = createContext<OuraDataContextType | undefined>(undefined);

/**
 * Props for OuraDataProvider component
 */
interface OuraDataProviderProperties {
  children: ReactNode;
}

/**
 * WHAT:
 *   Context provider that manages Oura health data with computed metrics
 *   and lazy loading.
 *
 * WHY:
 *   Enables global access to Oura data without prop drilling and provides
 *   computed metrics for common use cases.
 *
 * BEHAVIOR:
 *   - Lazy loads data only when explicitly requested
 *   - Computes HRV and heart rate metrics
 *   - Provides access to all Oura data fields
 *   - Manages loading and error states
 *
 * RISKS:
 *   - NOTE: Computed values are typed as any - may cause type safety issues
 *   - NOTE: Lazy loading means data may not be available on mount
 *   - NOTE: HRV calculations may not handle edge cases (empty arrays, null values)
 *   - NOTE: refreshData function is not implemented - may cause runtime errors
 *
 * @param root0
 *
 * @param root0.children
 *
 * @returns {JSX.Element} Oura data context provider component
 *
 */
export const OuraDataProvider: React.FC<OuraDataProviderProperties> = ({ children }) => {
  // NOTE: Disable automatic sync on mount - only manual or when Oura page is active
  const [data, setData] = useState<OuraDailyEntry | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // NOTE: Lazy load: Only load data when explicitly called
  const reload = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const latestEntry = await safeGetLatestOuraEntry();
      if (latestEntry === null) {
        setData(undefined);
      } else {
        setData(latestEntry);
      }
    } catch (error_) {
      const message = error_ instanceof Error ? error_.message : String(error_);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // NOTE: Computed values from data
  const readiness = data?.readiness;
  const sleep = data?.sleep;
  const activity = data?.activity;
  const heartrate = data?.heartrate;
  const fullSleep = data?.full_sleep;
  const spo2 = data?.spo2;
  const stress = data?.stress;
  const resilience = data?.resilience;
  const vascularAge = data?.vo2;

  /**
   * WHAT:
   *   Calculates average HRV from sleep data.
   *
   * WHY:
   *   Provides aggregate HRV metric for health assessment.
   *
   * BEHAVIOR:
   *   - Filters out null and NaN values
   *   - Calculates average of valid values
   *   - Returns undefined if no valid values
   */
  const calculateAverageHRV = useCallback(() => {
    if (!fullSleep?.hrv?.items) {
      return;
    }

    const validHRVValues = fullSleep.hrv.items.filter(
      (value): value is number => typeof value === "number" && !Number.isNaN(value),
    );

    if (validHRVValues.length === 0) {
      return;
    }

    const sum = validHRVValues.reduce((accumulator, value) => accumulator + value, 0);
    return sum / validHRVValues.length;
  }, [fullSleep?.hrv?.items]);

  /**
   * WHAT:
   *   Calculates maximum HRV from sleep data.
   *
   * WHY:
   *   Provides peak HRV metric for health assessment.
   */
  const calculateMaxHRV = useCallback(() => {
    if (!fullSleep?.hrv?.items) {
      return;
    }

    const validHRVValues = fullSleep.hrv.items.filter(
      (value): value is number => typeof value === "number" && !Number.isNaN(value),
    );

    if (validHRVValues.length === 0) {
      return;
    }

    return Math.max(...validHRVValues);
  }, [fullSleep?.hrv?.items]);

  /**
   * WHAT:
   *   Calculates average heart rate from sleep data.
   *
   * WHY:
   *   Provides aggregate heart rate metric for health assessment.
   */
  const calculateAverageHeartRate = useCallback(() => {
    if (!fullSleep?.heart_rate?.items) {
      return;
    }

    const validHeartRateValues = fullSleep.heart_rate.items.filter(
      (value): value is number => typeof value === "number" && !Number.isNaN(value),
    );

    if (validHeartRateValues.length === 0) {
      return;
    }

    const sum = validHeartRateValues.reduce((accumulator, value) => accumulator + value, 0);
    return sum / validHeartRateValues.length;
  }, [fullSleep?.heart_rate?.items]);

  // NOTE: Computed metrics
  const averageHRV = calculateAverageHRV();
  const maxHRV = calculateMaxHRV();
  const averageHeartRate = calculateAverageHeartRate();
  const hrvMeasurements = fullSleep?.hrv?.items ?? [];
  const heartRateMeasurements = fullSleep?.heart_rate?.items ?? [];

  /**
   * WHAT:
   *   Forces re-render of computed values.
   *
   * WHY:
   *   Triggers React to re-evaluate computed metrics.
   *
   * NOTE: This is a no-op but triggers React to re-evaluate - may not work as expected
   */
  const refreshData = useCallback(() => {
    // NOTE: Force re-render of computed values
    // NOTE: This is a no-op but triggers React to re-evaluate
  }, []);

  const contextValue: OuraDataContextType = {
    activity,
    averageHeartRate,
    averageHRV,
    data: data ?? null,
    error: error ?? null,
    fullSleep,
    heartrate,
    heartRateMeasurements,
    hrvMeasurements,
    loading,
    maxHRV,
    readiness,
    refreshData,
    reload,
    resilience,
    sleep,
    spo2,
    stress,
    vascularAge,
  };

  return <OuraDataContext.Provider value={contextValue}>{children}</OuraDataContext.Provider>;
};

/**
 * WHAT:
 *   Hook for accessing Oura data context.
 *
 * WHY:
 *   Provides type-safe access to Oura data throughout the application.
 *
 * BEHAVIOR:
 *   - Returns context value if available
 *   - Throws error if used outside provider
 *
 * RISKS:
 *   - NOTE: Error message may not be user-friendly
 *
 * @returns {OuraDataContextType} Oura data context value
 *
 */
export const useOuraData = (): OuraDataContextType => {
  const context = useContext(OuraDataContext);
  if (context === undefined) {
    throw new Error("useOuraData must be used within an OuraDataProvider");
  }
  return context;
};
